// Shared constants, theme, styles, and configuration
import API_BASE_URL from '../../api.js';

const SECTION_CONFIGS = {
  Observation: {
    icon: '👁',
    accentColor: '#0369a1',
    bgColor: '#f0f9ff',
    borderColor: '#7dd3fc',
    dashColor: '#38bdf8',
    labelColor: '#0c4a6e',
  },
  Palpation: {
    icon: '🤲',
    accentColor: '#b45309',
    bgColor: '#fffbeb',
    borderColor: '#fcd34d',
    dashColor: '#fbbf24',
    labelColor: '#78350f',
  },
  'Involuntary Movements': {
    icon: '⚡',
    accentColor: '#7c3aed',
    bgColor: '#f5f3ff',
    borderColor: '#c4b5fd',
    dashColor: '#a78bfa',
    labelColor: '#4c1d95',
  },
  'Muscle Girth': {
    icon: '📏',
    accentColor: '#0f766e',
    bgColor: '#f0fdfa',
    borderColor: '#5eead4',
    dashColor: '#2dd4bf',
    labelColor: '#134e4a',
  },
  'Sensory Testing (Core)': {
    icon: '🌡️',
    accentColor: '#b45309',
    bgColor: '#fffbeb',
    borderColor: '#fcd34d',
    dashColor: '#f59e0b',
    labelColor: '#78350f',
  },
  'Equilibrium': {
    icon: '⚖️',
    accentColor: '#0891b2',
    bgColor: '#ecfeff',
    borderColor: '#67e8f9',
    dashColor: '#22d3ee',
    labelColor: '#164e63',
  },
};

const isNormalOpt = (opt) => {
  const l = opt.toLowerCase().trim();
  return (
    l === 'normal' || l.startsWith('normal ') || l === 'normal (early)' ||
    l === 'absent' || l === 'none' || l.startsWith('no ') ||
    l === 'intact' || l === 'full' || l === 'normal resting tone' ||
    l === 'no joint abnormality' || l === 'normal skin texture' ||
    l === 'normal build' || l === 'normal strength' ||
    l === 'no wasting' || l === 'continent' ||
    l === 'negative (normal)' || l === 'no deficit' ||
    l === 'normal (stereognosis intact)' || l === 'normal (no extinction)' ||
    l === 'none' || l === 'normal bilaterally' || l === 'none (normal)'
  );
};


const STEPS = ["Pattern", "Patient ID", "Subjective", "Objective", "Clinical Impression"];
const NEURO_DRAFT_KEY = 'neuro-assessment-draft';
const SAVED_NEURO_ASSESSMENTS_KEY = 'saved-neuro-assessments';
const MAX_CHAT_HISTORY = 20;

const theme = {
  primary: '#4f46e5',
  primaryDark: '#4338ca',
  secondary: '#f8fafc',
  accent: '#0ea5e9',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#10b981',
  textMain: '#0f172a',
  textMuted: '#64748b',
  border: '#e2e8f0',
  surface: '#ffffff',
  background: '#f1f5f9'
};

const styles = {
  appWrapper: { minHeight: '100vh', backgroundColor: theme.background, padding: '0', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: theme.textMain },
  container: { maxWidth: '860px', margin: '0 auto', padding: '20px 16px 96px' },
  headerCard: { backgroundColor: theme.surface, borderRadius: '16px', padding: '24px 20px', marginBottom: '16px', border: `1px solid ${theme.border}` },
  title: { fontSize: '1.8rem', fontWeight: '800', margin: '0 0 4px 0', color: theme.textMain, letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.95rem', color: theme.textMuted, margin: 0, fontWeight: '500' },
  stepperContainer: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', position: 'relative' },
  stepperLine: { position: 'absolute', top: '20px', left: '10%', right: '10%', height: '3px', backgroundColor: theme.border, zIndex: 1 },
  stepItem: { position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', width: '20%' },
  stepCircle: { width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1rem', transition: 'all 0.3s', backgroundColor: theme.surface, border: `2px solid ${theme.border}`, color: theme.textMuted },
  stepCircleActive: { borderColor: theme.primary, backgroundColor: theme.primary, color: '#fff', boxShadow: `0 0 0 4px ${theme.primary}22` },
  stepCircleCompleted: { borderColor: theme.success, backgroundColor: theme.success, color: '#fff' },
  stepLabel: { fontSize: '0.75rem', fontWeight: '600', color: theme.textMuted, textAlign: 'center', transition: 'color 0.3s' },
  stepLabelActive: { color: theme.primary, fontWeight: '700' },
  zoneGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', paddingBottom: '20px' },
  zoneCard: { backgroundColor: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '16px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '8px' },
  zoneCardHover: { borderColor: '#a5b4fc', boxShadow: '0 4px 16px rgba(79,70,229,0.1)' },
  sectionCard: { backgroundColor: theme.surface, borderRadius: '16px', marginBottom: '16px', padding: '24px 20px', border: `1px solid ${theme.border}`, animation: 'fadeIn 0.3s ease-out' },
  sectionTitle: { fontSize: '1.2rem', fontWeight: '800', color: theme.textMain, marginBottom: '20px', paddingBottom: '12px', borderBottom: `1px solid ${theme.border}` },
  gridContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '0.875rem', fontWeight: '700', color: theme.textMain, letterSpacing: '0.01em' },
  input: { padding: '14px 16px', borderRadius: '12px', border: `1.5px solid ${theme.border}`, fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s', backgroundColor: '#f8fafc', color: theme.textMain, width: '100%', boxSizing: 'border-box', fontWeight: '500', minHeight: 52 },
  inputFocus: { backgroundColor: '#fff', borderColor: theme.primary },
  textarea: { minHeight: '100px', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5' },
  subBox: { backgroundColor: '#f8fafc', borderRadius: '12px', padding: '20px', border: `1px solid ${theme.border}`, marginBottom: '16px' },
  subBoxTitle: { fontSize: '0.95rem', fontWeight: '800', color: theme.textMain, margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  chip: { padding: '8px 14px', borderRadius: '99px', border: `1.5px solid ${theme.border}`, backgroundColor: theme.surface, color: '#475569', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s', minHeight: '44px', display: 'inline-flex', alignItems: 'center' },
  chipActive: { backgroundColor: theme.primary, color: '#fff', borderColor: theme.primary },
  dynamicRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr)) auto', gap: '12px', alignItems: 'center', backgroundColor: '#fff', padding: '14px 16px', borderRadius: '12px', marginBottom: '12px', border: `1px solid ${theme.border}` },
  addBtn: { width: '100%', padding: '13px', borderRadius: '12px', border: `2px dashed #cbd5e1`, backgroundColor: '#f8fafc', color: theme.primary, fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' },
  /* Fixed bottom footer */
  footerBar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: `1px solid ${theme.border}`, padding: '12px 16px', zIndex: 1000 },
  footerInner: { maxWidth: '860px', margin: '0 auto' },
  footerBtn: { display: 'block', width: '100%', padding: '16px', borderRadius: '14px', background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)', color: '#fff', border: 'none', fontWeight: '700', fontSize: '1rem', cursor: 'pointer', minHeight: 54, letterSpacing: '-0.01em', transition: 'opacity 0.15s' },
  footerBtnSuccess: { display: 'block', width: '100%', padding: '16px', borderRadius: '14px', background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', color: '#fff', border: 'none', fontWeight: '700', fontSize: '1rem', cursor: 'pointer', minHeight: 54 },
  /* legacy */
  stickyFooter: { position: 'sticky', bottom: 20, left: 0, right: 0, backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', padding: '20px 30px', border: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', zIndex: 100, borderRadius: '24px', maxWidth: '860px', margin: '0 auto' },
  navBtn: { padding: '14px 28px', borderRadius: '12px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.2s' },
  btnSecondary: { backgroundColor: '#f1f5f9', color: theme.textMain, border: `1px solid ${theme.border}` },
  btnPrimary: { background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)', color: '#fff', border: 'none' },
};

const DEFAULT_FORM_DATA = {
  name: '', date: new Date().toISOString().split('T')[0], age: '', gender: '', ipOp: 'OP', occupation: '',
  address: '', phone: '', regNo: '', civilStatus: '', diagnosis: '',
  chiefComplaint: '', hpiText: '', historyMedical: '', historyPersonal: '', historyFamily: '',
  symSide: '', symOnset: '', symDuration: '', symSeverity: '',
  investigation_list: [],
  problem_list: [],
  functionalDiagnosis: '', shortTermGoals: '', longTermGoals: '', treatmentPlan: ''
};

export { SECTION_CONFIGS, isNormalOpt, STEPS, NEURO_DRAFT_KEY, SAVED_NEURO_ASSESSMENTS_KEY, API_BASE_URL, MAX_CHAT_HISTORY, theme, styles, DEFAULT_FORM_DATA };
